import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import EtfTickerLink from '../EtfTickerLink';
import { useAuth } from '../../context/AuthContext';
import { initAnalytics } from '../../lib/analytics';
import { reportCdnFallback } from '../../lib/cdnFallbackTelemetry';
import { useAuthenticatedFetch } from '../../utils/apiClient';
import InfoTooltipIcon from '../ui/InfoTooltipIcon';
import DashboardDateInput from './DashboardDateInput';
import {
  MIN_DASHBOARD_DATE,
  SNAPSHOT_META_PATH,
  createSnapshotRequestFailure,
  fetchSnapshotResponse,
  getUtcCutoffDate,
  isIsoDate,
  isSnapshotRunKind,
  readSnapshotRequestFailure,
  resolveSnapshotPayloadPath,
  resolveSnapshotTemplatePath,
  type SnapshotFailureStage,
  type SnapshotRunKind,
} from './snapshotDelivery';
import { useHistoricalDateRefreshToken } from './useHistoricalDateRefresh';

type DashboardAccessLevel = 'FULL' | 'PREVIEW';
type DashboardUserStatus = 'anonymous' | 'free' | 'premium';

type DashboardAccess = {
  level: DashboardAccessLevel;
  user_status: DashboardUserStatus;
  cutoff_date: string;
  sample_date: string;
};

type DashboardAsset = {
  ticker: string;
  description: string;
  asset_class?: string | null;
  rank_change?: number | null;
  streak_days?: number | null;
  streak_start_date?: string | null;
  streak_price_change_abs?: number | null;
  streak_price_change_pct?: number | null;
  streak_loading?: boolean;
};

type StreakRankBasis = 'rank' | 'rsi5_rank';

type StreakRequestItem = {
  key: string;
  ticker: string;
  matrixgroup: string;
  as_of_date: string;
  top_limit: number;
  rank_basis: StreakRankBasis;
};

type StreakResponseItem = {
  key: string;
  streak_days: number | null;
  streak_start_date: string | null;
  price_change_abs: number | null;
  price_change_pct: number | null;
};

type AssetBlock = {
  as_of_date?: string | null;
  top_assets: DashboardAsset[];
};

type CrossAssetDashboardResponse = {
  date: string;
  access: DashboardAccess;
  asset_classes: {
    macro: AssetBlock & { allocation: number };
    bonds: AssetBlock & { allocation: number };
    commodities: AssetBlock & { allocation: number };
    currencies: AssetBlock & { allocation: number };
    crypto: {
      as_of_date?: string | null;
      allocation: number;
      top_assets: DashboardAsset[];
    };
    equities: {
      allocation: number;
      countries: AssetBlock;
      sp_sectors: AssetBlock;
      etfs: AssetBlock;
    };
  };
};

type CrossAssetSnapshotMeta = {
  latest_date?: string;
  latest_run_kind?: SnapshotRunKind;
  cutoff_date_utc?: string;
  min_date?: string;
  cross_asset?: {
    preview_template?: string;
    delayed_template?: string;
    full_template?: string;
    preview_ref_template?: string;
    delayed_ref_template?: string;
    full_ref_template?: string;
  };
};

type AssetSubSection = {
  title: string;
  assets: DashboardAsset[];
};

type AssetClassTowerProps = {
  name: string;
  color: string;
  selectedDate: string;
  asOfDate?: string | null;
  cardPath?: string;
  assetClickable?: boolean;
  rankChangeBasis?: string;
  assets?: DashboardAsset[];
  subSections?: AssetSubSection[];
  metaLabel?: string;
  previewLocked?: boolean;
  lockOverlay?: React.ReactNode;
  macroRepresentation?: MacroRepresentationSummary | null;
  showMacroAssetClassPills?: boolean;
};

type EquitiesSegmentKey = 'global' | 'thematic' | 'sp500';

type EquitiesSegment = {
  key: EquitiesSegmentKey;
  label: string;
  assets: DashboardAsset[];
  asOfDate?: string | null;
};

type MacroRepresentationKey = 'Commodities' | 'Bonds' | 'FX' | 'Equities' | 'Crypto' | 'Other';

type MacroRepresentationSegment = {
  key: MacroRepresentationKey;
  label: string;
  shortLabel: string;
  count: number;
  pct: number;
  color: string;
};

type MacroRepresentationSummary = {
  totalCount: number;
  segments: MacroRepresentationSegment[];
};

const DEFAULT_CROSS_ASSET_REF_TEMPLATE = '/v1/cross-asset/{tier}-runs/{run_kind}/refs/{date}.json';

const formatSearchDate = (value: string): string => {
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const toSafeAssets = (assets: DashboardAsset[] | undefined | null): DashboardAsset[] => {
  if (!Array.isArray(assets)) return [];
  return assets
    .filter((asset) => asset && typeof asset.ticker === 'string')
    .map((asset) => ({
      ...asset,
      ticker: asset.ticker,
      description: asset.description || asset.ticker,
      asset_class: typeof asset.asset_class === 'string' ? asset.asset_class : null,
      rank_change: typeof asset.rank_change === 'number' ? asset.rank_change : null,
      streak_days: typeof asset.streak_days === 'number' ? asset.streak_days : null,
      streak_start_date: typeof asset.streak_start_date === 'string' ? asset.streak_start_date : null,
      streak_price_change_abs:
        typeof asset.streak_price_change_abs === 'number' ? asset.streak_price_change_abs : null,
      streak_price_change_pct:
        typeof asset.streak_price_change_pct === 'number' ? asset.streak_price_change_pct : null,
      streak_loading: !!asset.streak_loading,
    }));
};

const buildTopLabel = (count: number): string => `Top ${count}`;

const buildStreakKey = (
  matrixgroup: string,
  asOfDate: string,
  rankBasis: StreakRankBasis,
  topLimit: number,
  ticker: string
): string => `${matrixgroup}|${asOfDate}|${rankBasis}|${topLimit}|${ticker}`;

const buildTopMetaLabel = (count: number, selectedDate: string, asOfDate?: string | null): string => {
  if (asOfDate && selectedDate && asOfDate !== selectedDate) {
    return `${buildTopLabel(count)} · As of ${formatSearchDate(asOfDate)}`;
  }
  return buildTopLabel(count);
};

const MACRO_REPRESENTATION_ORDER: Record<MacroRepresentationKey, number> = {
  Commodities: 0,
  Bonds: 1,
  FX: 2,
  Equities: 3,
  Crypto: 4,
  Other: 5,
};

const MACRO_REPRESENTATION_STYLE: Record<
  MacroRepresentationKey,
  {
    label: string;
    shortLabel: string;
    color: string;
    pillBackground: string;
    pillBorder: string;
    pillText: string;
  }
> = {
  Commodities: {
    label: 'Commodities',
    shortLabel: 'Cmdty',
    color: 'var(--qcl-cmdty)',
    pillBackground: 'rgba(232,147,12,0.10)',
    pillBorder: 'rgba(232,147,12,0.22)',
    pillText: 'var(--qcl-cmdty)',
  },
  Bonds: {
    label: 'Bonds',
    shortLabel: 'Bonds',
    color: 'var(--qcl-bonds)',
    pillBackground: 'rgba(67,97,238,0.10)',
    pillBorder: 'rgba(67,97,238,0.22)',
    pillText: 'var(--qcl-bonds)',
  },
  FX: {
    label: 'FX',
    shortLabel: 'FX',
    color: 'var(--qcl-fx)',
    pillBackground: 'rgba(6,182,140,0.10)',
    pillBorder: 'rgba(6,182,140,0.22)',
    pillText: 'var(--qcl-fx)',
  },
  Equities: {
    label: 'Equities',
    shortLabel: 'Eq',
    color: 'var(--qcl-eq)',
    pillBackground: 'rgba(239,68,68,0.10)',
    pillBorder: 'rgba(239,68,68,0.22)',
    pillText: 'var(--qcl-eq)',
  },
  Crypto: {
    label: 'Crypto',
    shortLabel: 'Crypto',
    color: 'var(--qcl-crypto)',
    pillBackground: 'rgba(16,185,129,0.10)',
    pillBorder: 'rgba(16,185,129,0.22)',
    pillText: 'var(--qcl-crypto)',
  },
  Other: {
    label: 'Other',
    shortLabel: 'Other',
    color: '#94a3b8',
    pillBackground: 'rgba(148,163,184,0.12)',
    pillBorder: 'rgba(148,163,184,0.24)',
    pillText: '#64748b',
  },
};

const normalizeMacroRepresentationKey = (assetClass: string | null | undefined): MacroRepresentationKey => {
  const value = (assetClass || '').trim().toLowerCase();
  if (value === 'commodities' || value === 'commodity') return 'Commodities';
  if (value === 'bonds' || value === 'bond') return 'Bonds';
  if (value === 'currencies' || value === 'currency' || value === 'fx' || value === 'foreign exchange') {
    return 'FX';
  }
  if (value === 'equities' || value === 'equity') return 'Equities';
  if (value === 'cryptocurrency' || value === 'crypto' || value === 'cryptocurrencies') return 'Crypto';
  return 'Other';
};

const roundPercentagesTo100 = (counts: number[], totalCount: number): number[] => {
  if (totalCount <= 0 || counts.length === 0) return [];
  const parts = counts.map((count, index) => {
    const exact = (count * 100) / totalCount;
    const floor = Math.floor(exact);
    return { index, floor, remainder: exact - floor };
  });
  let remainder = 100 - parts.reduce((sum, part) => sum + part.floor, 0);
  parts
    .slice()
    .sort((a, b) => {
      if (b.remainder !== a.remainder) return b.remainder - a.remainder;
      return a.index - b.index;
    })
    .forEach((part) => {
      if (remainder <= 0) return;
      part.floor += 1;
      remainder -= 1;
    });

  return parts.map((part) => part.floor);
};

const buildMacroRepresentationSummary = (assets: DashboardAsset[]): MacroRepresentationSummary | null => {
  if (!Array.isArray(assets) || assets.length === 0) return null;

  const classifiedAssets = assets.filter(
    (asset) => typeof asset?.asset_class === 'string' && asset.asset_class.trim().length > 0
  );
  if (classifiedAssets.length === 0) return null;

  const countsByKey = classifiedAssets.reduce((accumulator, asset) => {
    const key = normalizeMacroRepresentationKey(asset.asset_class);
    accumulator.set(key, (accumulator.get(key) || 0) + 1);
    return accumulator;
  }, new Map<MacroRepresentationKey, number>());

  const orderedKeys = Array.from(countsByKey.keys()).sort((left, right) => {
    const countDiff = (countsByKey.get(right) || 0) - (countsByKey.get(left) || 0);
    if (countDiff !== 0) return countDiff;
    return MACRO_REPRESENTATION_ORDER[left] - MACRO_REPRESENTATION_ORDER[right];
  });
  const counts = orderedKeys.map((key) => countsByKey.get(key) || 0);
  const pcts = roundPercentagesTo100(counts, classifiedAssets.length);

  return {
    totalCount: classifiedAssets.length,
    segments: orderedKeys.map((key, index) => {
      const style = MACRO_REPRESENTATION_STYLE[key];
      return {
        key,
        label: style.label,
        shortLabel: style.shortLabel,
        count: counts[index],
        pct: pcts[index] ?? 0,
        color: style.color,
      };
    }),
  };
};

const getMacroAssetClassPillStyle = (assetClass: string | null | undefined) => {
  if (!assetClass || !assetClass.trim()) return null;
  const key = normalizeMacroRepresentationKey(assetClass);
  const style = MACRO_REPRESENTATION_STYLE[key];
  return {
    key,
    label: style.label,
    shortLabel: style.shortLabel,
    color: style.pillText,
    background: style.pillBackground,
    borderColor: style.pillBorder,
  };
};

export const AssetRow: React.FC<{
  asset: DashboardAsset;
  color: string;
  index: number;
  rankChangeBasis?: string;
  isEtfClickable?: boolean;
  showMacroAssetClassPill?: boolean;
}> = ({ asset, color, index, rankChangeBasis = 'Rank', isEtfClickable = true, showMacroAssetClassPill = false }) => {
  const rankChange = typeof asset.rank_change === 'number' ? asset.rank_change : null;
  const isImproved = rankChange !== null && rankChange > 0;
  const isWorsened = rankChange !== null && rankChange < 0;
  const rankChangeLabel = rankChange === null ? '0' : String(Math.abs(rankChange));
  const rankChangeTooltip =
    rankChange === null
      ? `${rankChangeBasis} change vs previous available date: no prior data (displayed as 0).`
      : `${rankChangeBasis} change vs previous available date: ${
          isImproved ? `improved by ${rankChangeLabel}` : isWorsened ? `worsened by ${rankChangeLabel}` : 'no change'
        }.`;

  const streakDays = typeof asset.streak_days === 'number' ? asset.streak_days : null;
  const streakChangePct =
    typeof asset.streak_price_change_pct === 'number' ? asset.streak_price_change_pct : null;
  const streakChangeAbs =
    typeof asset.streak_price_change_abs === 'number' ? asset.streak_price_change_abs : null;
  const streakLoading = !!asset.streak_loading;
  const streakDaysLabel = streakLoading ? '…' : streakDays === null ? 'n/a' : `${streakDays}d`;
  const streakPctLabel = streakLoading
    ? '…'
    : streakChangePct === null
    ? 'n/a'
    : `${streakChangePct > 0 ? '+' : ''}${streakChangePct.toFixed(1)}%`;
  const streakChangeClass =
    streakChangePct === null
      ? 'text-[var(--qcl-text-muted)]'
      : streakChangePct > 0
      ? 'text-emerald-600'
      : streakChangePct < 0
      ? 'text-rose-600'
      : 'text-[var(--qcl-text-muted)]';
  const streakTooltip = streakLoading
    ? 'Loading streak metrics...'
    : streakDays === null
    ? 'Streak metrics unavailable.'
    : `Current Top streak: ${streakDays} data day${streakDays === 1 ? '' : 's'}${
        asset.streak_start_date ? ` (since ${formatSearchDate(asset.streak_start_date)}).` : '.'
      }${
        streakChangePct === null || streakChangeAbs === null
          ? ''
          : ` Price change since streak start: ${streakChangeAbs > 0 ? '+' : ''}${streakChangeAbs.toFixed(
              4
            )} (${streakChangePct > 0 ? '+' : ''}${streakChangePct.toFixed(1)}%).`
      }`;
  const macroAssetClassPill = showMacroAssetClassPill ? getMacroAssetClassPillStyle(asset.asset_class) : null;

  const rowContent = (
    <>
      <span className="qcl-mono w-[18px] text-center text-[10px] text-[var(--qcl-text-muted)]">{index + 1}</span>
      <span className="qcl-mono min-w-[54px] text-[13px] font-semibold" style={{ color }}>
        {asset.ticker}
      </span>
      <span className="min-w-0 flex-1 truncate text-[12px] text-[var(--qcl-text-dim)]">{asset.description}</span>
      {macroAssetClassPill ? (
        <span
          className="shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold leading-none"
          style={{
            color: macroAssetClassPill.color,
            background: macroAssetClassPill.background,
            borderColor: macroAssetClassPill.borderColor,
          }}
          title={`Asset class: ${macroAssetClassPill.label}`}
        >
          <span className="md:hidden">{macroAssetClassPill.shortLabel}</span>
          <span className="hidden md:inline">{macroAssetClassPill.label}</span>
        </span>
      ) : null}
      <span className={`flex ${macroAssetClassPill ? 'min-w-[88px]' : 'min-w-[96px]'} flex-col items-end gap-0.5`}>
        <span className="inline-flex items-center gap-1">
          <span
            className={`qcl-mono text-right text-[11px] font-semibold ${
              isImproved ? 'text-emerald-600' : isWorsened ? 'text-rose-600' : 'text-[var(--qcl-text-muted)]'
            }`}
            aria-label={`${rankChangeBasis} change ${rankChangeLabel}`}
            title={rankChangeTooltip}
          >
            {isImproved ? '↑' : isWorsened ? '↓' : '→'} {rankChangeLabel}
          </span>
          <InfoTooltipIcon tooltip={rankChangeTooltip} className="h-3.5 w-3.5 border-[var(--qcl-border-light)] text-[9px]" />
        </span>
        <span className="inline-flex items-center gap-1">
          <span
            className={`inline-flex min-w-[68px] items-center justify-end gap-1 rounded px-1.5 py-0.5 ${
              streakLoading
                ? 'bg-[var(--qcl-bg)] text-[var(--qcl-text-muted)] animate-pulse'
                : ''
            }`}
            aria-label={`Top streak ${streakDaysLabel}, price change ${streakPctLabel}`}
            title={streakTooltip}
          >
            <span className="qcl-mono text-right text-[10px] text-[var(--qcl-text-muted)]">{streakDaysLabel}</span>
            <span className="text-[var(--qcl-text-muted)]">·</span>
            <span className={`qcl-mono text-right text-[10px] ${streakChangeClass}`}>{streakPctLabel}</span>
          </span>
          <InfoTooltipIcon tooltip={streakTooltip} className="h-3.5 w-3.5 border-[var(--qcl-border-light)] text-[9px]" />
        </span>
      </span>
      {isEtfClickable ? <span className="text-sm text-[var(--qcl-text-muted)]">›</span> : null}
    </>
  );

  if (isEtfClickable) {
    return (
      <EtfTickerLink
        ticker={asset.ticker}
        backLabel="Cross-Asset"
        className="flex min-h-[44px] items-center gap-2.5 border-b border-[var(--qcl-border-light)] px-[14px] py-2.5 transition-colors hover:bg-[var(--qcl-card-hover)] last:border-b-0 md:px-[18px]"
      >
        {rowContent}
      </EtfTickerLink>
    );
  }

  return (
    <div className="flex min-h-[44px] items-center gap-2.5 border-b border-[var(--qcl-border-light)] px-[14px] py-2.5 last:border-b-0 md:px-[18px]">
      {rowContent}
    </div>
  );
};

const AssetClassTower: React.FC<AssetClassTowerProps> = ({
  name,
  color,
  selectedDate,
  asOfDate,
  cardPath,
  assetClickable = true,
  rankChangeBasis = 'Rank',
  assets,
  subSections,
  metaLabel,
  previewLocked = false,
  lockOverlay,
  macroRepresentation,
  showMacroAssetClassPills = false,
}) => {
  const safeAssets = toSafeAssets(assets);
  const safeSubSections = (subSections || []).map((section) => ({
    title: section.title,
    assets: toSafeAssets(section.assets),
  }));
  const holdingsCount =
    safeSubSections.length > 0
      ? safeSubSections.reduce((sum, section) => sum + section.assets.length, 0)
      : safeAssets.length;
  const meta = metaLabel || buildTopMetaLabel(holdingsCount, selectedDate, asOfDate);

  const displayedAssets = previewLocked ? safeAssets.slice(0, 1) : safeAssets;
  const displayedSubSections = previewLocked
    ? safeSubSections.map((section) => ({ ...section, assets: section.assets.slice(0, 1) }))
    : safeSubSections;
  const shouldRenderLockOverlay = previewLocked && !!lockOverlay;
  const macroRepresentationGridTemplate =
    macroRepresentation?.segments?.length
      ? macroRepresentation.segments.map((segment) => `${segment.pct}%`).join(' ')
      : '';

  return (
    <section className="overflow-hidden rounded-[14px] border border-[var(--qcl-border)] bg-white shadow-[var(--qcl-shadow-sm)]">
      <div className="h-[3px]" style={{ background: color }} />
      {cardPath ? (
        <Link
          to={cardPath}
          className="flex items-center gap-3 border-b border-[var(--qcl-border-light)] px-[14px] py-3 transition-colors hover:bg-[var(--qcl-card-hover)] md:px-[18px]"
          aria-label={`Open ${name} dashboard`}
        >
          <div className="h-9 w-1 rounded-[2px]" style={{ background: color }} />
          <div className="min-w-0 flex-1">
            <h3 className="text-[15px] font-bold tracking-[-0.012em] text-[var(--qcl-text)]">{name}</h3>
            <p className="text-[11px] font-medium text-[var(--qcl-text-dim)]">{meta}</p>
          </div>
          <span className="text-[15px] text-[var(--qcl-text-muted)]">›</span>
        </Link>
      ) : (
        <div className="flex items-center gap-3 border-b border-[var(--qcl-border-light)] px-[14px] py-3 md:px-[18px]">
          <div className="h-9 w-1 rounded-[2px]" style={{ background: color }} />
          <div className="min-w-0 flex-1">
            <h3 className="text-[15px] font-bold tracking-[-0.012em] text-[var(--qcl-text)]">{name}</h3>
            <p className="text-[11px] font-medium text-[var(--qcl-text-dim)]">{meta}</p>
          </div>
        </div>
      )}

      {macroRepresentation && macroRepresentation.segments.length > 0 ? (
        <div className="border-b border-[var(--qcl-border-light)] px-[14px] py-3 md:px-[18px]">
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--qcl-text-muted)]">
            Macro Representation (Top {macroRepresentation.totalCount})
          </div>
          <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-[var(--qcl-bg)]">
            <div className="flex h-full w-full">
              {macroRepresentation.segments.map((segment) => (
                <div key={`${name}-macro-segment-${segment.key}`} style={{ width: `${segment.pct}%`, background: segment.color }} />
              ))}
            </div>
          </div>
          <div
            className="mt-2 grid items-start text-[10px] md:text-[11px]"
            style={{ gridTemplateColumns: macroRepresentationGridTemplate }}
          >
            {macroRepresentation.segments.map((segment) => (
              <div key={`${name}-macro-legend-${segment.key}`} className="min-w-0 px-1 text-center leading-tight">
                <span className="block font-semibold text-[var(--qcl-text)]">
                  <span className="md:hidden">{segment.shortLabel}</span>
                  <span className="hidden md:inline">{segment.label}</span> {segment.pct}%
                </span>
                <span className="qcl-mono block text-[10px] text-[var(--qcl-text-dim)]">
                  {segment.count}/{macroRepresentation.totalCount}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {displayedSubSections.length > 0 ? (
        displayedSubSections.map((section) => (
          <div key={section.title}>
            <div className="border-b border-[var(--qcl-border-light)] bg-[var(--qcl-surface-muted)] px-[14px] py-1.5 text-[9px] font-bold uppercase tracking-[1px] text-[var(--qcl-text-muted)] md:px-[18px]">
              {section.title}
            </div>
            {section.assets.length > 0 ? (
              section.assets.map((asset, index) => (
                <AssetRow
                  key={`${section.title}-${asset.ticker}`}
                  asset={asset}
                  color={color}
                  index={index}
                  rankChangeBasis={rankChangeBasis}
                  isEtfClickable={assetClickable}
                  showMacroAssetClassPill={showMacroAssetClassPills}
                />
              ))
            ) : (
              <div className="px-[14px] py-3 text-[12px] text-[var(--qcl-text-dim)] md:px-[18px]">No holdings for this segment.</div>
            )}
          </div>
        ))
      ) : displayedAssets.length > 0 ? (
        displayedAssets.map((asset, index) => (
          <AssetRow
            key={asset.ticker}
            asset={asset}
            color={color}
            index={index}
            rankChangeBasis={rankChangeBasis}
            isEtfClickable={assetClickable}
            showMacroAssetClassPill={showMacroAssetClassPills}
          />
        ))
      ) : (
        <div className="px-[14px] py-3 text-[12px] text-[var(--qcl-text-dim)] md:px-[18px]">No holdings available.</div>
      )}

      {shouldRenderLockOverlay ? (
        <div className="relative border-t border-[var(--qcl-border-light)]">
          <div className="pointer-events-none select-none blur-[2px]">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={`${name}-locked-${index}`}
                className="flex min-h-[44px] items-center gap-2.5 border-b border-[var(--qcl-border-light)] px-[14px] py-2.5 last:border-b-0 md:px-[18px]"
              >
                <span className="h-2.5 w-4 rounded bg-[var(--qcl-border-light)]" />
                <span className="h-2.5 w-12 rounded bg-[var(--qcl-border-light)]" />
                <span className="h-2.5 flex-1 rounded bg-[var(--qcl-border-light)]" />
              </div>
            ))}
          </div>
          <div className="absolute inset-0 flex items-center justify-center bg-[var(--qcl-overlay)] px-3 py-2">
            {lockOverlay}
          </div>
        </div>
      ) : null}
    </section>
  );
};

const EquitiesTower: React.FC<{
  selectedDate: string;
  segments: EquitiesSegment[];
  previewLocked?: boolean;
  lockOverlay?: React.ReactNode;
}> = ({ selectedDate, segments, previewLocked = false, lockOverlay }) => {
  const [activeSegment, setActiveSegment] = useState<EquitiesSegmentKey>('global');

  const visibleSegments = previewLocked
    ? segments.map((segment) => ({ ...segment, assets: segment.assets.slice(0, 1) }))
    : segments;
  const activeVisibleSegment =
    visibleSegments.find((segment) => segment.key === activeSegment) || visibleSegments[0];
  const shouldRenderLockOverlay = previewLocked && !!lockOverlay;

  return (
    <section className="overflow-hidden rounded-[14px] border border-[var(--qcl-border)] bg-white shadow-[var(--qcl-shadow-sm)]">
      <div className="h-[3px] bg-[var(--qcl-eq)]" />
      <div className="flex items-center gap-3 border-b border-[var(--qcl-border-light)] px-[14px] py-3 md:px-[18px]">
        <div className="h-9 w-1 rounded-[2px] bg-[var(--qcl-eq)]" />
        <div className="min-w-0 flex-1">
          <h3 className="text-[15px] font-bold tracking-[-0.012em] text-[var(--qcl-text)]">Equities</h3>
        </div>
      </div>

      <div className="px-[14px] pt-3 md:px-[18px]">
        <div className="flex gap-0.5 rounded-[10px] bg-[var(--qcl-bg)] p-[3px]">
          {visibleSegments.map((segment) => (
            <button
              key={segment.key}
              type="button"
              onClick={() => setActiveSegment(segment.key)}
              className={`min-h-[44px] flex-1 rounded-[8px] px-2 py-2 text-[12px] font-semibold transition ${
                activeSegment === segment.key
                  ? 'bg-white text-[var(--qcl-text)] shadow-[0_1px_4px_rgba(0,0,0,0.08)]'
                  : 'text-[var(--qcl-text-dim)] hover:text-[var(--qcl-text)]'
              }`}
            >
              {segment.label}
            </button>
          ))}
        </div>
      </div>

      <div className="border-b border-[var(--qcl-border-light)] px-[14px] py-2 text-[11px] font-medium text-[var(--qcl-text-dim)] md:px-[18px]">
        {buildTopMetaLabel(activeVisibleSegment?.assets.length || 0, selectedDate, activeVisibleSegment?.asOfDate)}
      </div>

      {activeVisibleSegment?.assets.length ? (
        activeVisibleSegment.assets.map((asset, index) => (
          <AssetRow
            key={`${activeVisibleSegment.key}-${asset.ticker}`}
            asset={asset}
            color="var(--qcl-eq)"
            index={index}
            isEtfClickable={!previewLocked}
          />
        ))
      ) : (
        <div className="px-[14px] py-3 text-[12px] text-[var(--qcl-text-dim)] md:px-[18px]">No holdings available.</div>
      )}

      {shouldRenderLockOverlay ? (
        <div className="relative border-t border-[var(--qcl-border-light)]">
          <div className="pointer-events-none select-none blur-[2px]">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={`equities-locked-${index}`}
                className="flex min-h-[44px] items-center gap-2.5 border-b border-[var(--qcl-border-light)] px-[14px] py-2.5 last:border-b-0 md:px-[18px]"
              >
                <span className="h-2.5 w-4 rounded bg-[var(--qcl-border-light)]" />
                <span className="h-2.5 w-12 rounded bg-[var(--qcl-border-light)]" />
                <span className="h-2.5 flex-1 rounded bg-[var(--qcl-border-light)]" />
              </div>
            ))}
          </div>
          <div className="absolute inset-0 flex items-center justify-center bg-[var(--qcl-overlay)] px-3 py-2">
            {lockOverlay}
          </div>
        </div>
      ) : null}
    </section>
  );
};

export const DashboardDatePicker: React.FC<{
  dateValue: string;
  dateLabel: string;
  onDateChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({ dateValue, dateLabel, onDateChange }) => (
  <section className="rounded-[14px] border border-[var(--qcl-border)] bg-white p-[14px] shadow-[var(--qcl-shadow-sm)] md:p-[18px]">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h2 className="text-[11px] font-bold uppercase tracking-[1px] text-[var(--qcl-text-muted)]">Dashboard Date</h2>
      <DashboardDateInput
        value={dateValue}
        min={MIN_DASHBOARD_DATE}
        onChange={onDateChange}
        ariaLabel="Select dashboard date"
        displayValue={dateLabel}
        title={dateLabel}
        wrapperClassName="w-full md:w-[184px]"
      />
    </div>
  </section>
);

const DashboardSkeleton: React.FC = () => (
  <div className="mx-auto max-w-[920px] space-y-3 px-4 py-[14px] md:space-y-4 md:px-7 md:py-5">
    <section className="rounded-[14px] border border-[var(--qcl-border)] bg-white p-[14px] shadow-[var(--qcl-shadow-sm)] md:p-[18px]">
      <div className="flex items-center justify-between gap-3">
        <div className="h-3 w-24 animate-pulse rounded bg-[var(--qcl-border)]" />
        <div className="h-10 w-36 animate-pulse rounded-[10px] bg-[var(--qcl-border-light)]" />
      </div>
    </section>
    {Array.from({ length: 6 }).map((_, index) => (
      <section key={index} className="overflow-hidden rounded-[14px] border border-[var(--qcl-border)] bg-white shadow-[var(--qcl-shadow-sm)]">
        <div className="h-[3px] w-[45%] animate-pulse bg-[var(--qcl-border-light)]" />
        <div className="flex items-center justify-between gap-3 border-b border-[var(--qcl-border-light)] px-[14px] py-3 md:px-[18px]">
          <div className="h-9 w-1 animate-pulse rounded bg-[var(--qcl-border)]" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-28 animate-pulse rounded bg-[var(--qcl-border)]" />
            <div className="h-3 w-40 animate-pulse rounded bg-[var(--qcl-border-light)]" />
          </div>
        </div>
        <div className="space-y-0">
          {Array.from({ length: 3 }).map((_, rowIndex) => (
            <div key={rowIndex} className="flex min-h-[44px] items-center gap-3 border-b border-[var(--qcl-border-light)] px-[14px] py-2.5 last:border-b-0 md:px-[18px]">
              <div className="h-3 w-3 animate-pulse rounded bg-[var(--qcl-border)]" />
              <div className="h-3 w-14 animate-pulse rounded bg-[var(--qcl-border)]" />
              <div className="h-3 flex-1 animate-pulse rounded bg-[var(--qcl-border-light)]" />
              <div className="h-3 w-3 animate-pulse rounded bg-[var(--qcl-border)]" />
            </div>
          ))}
        </div>
      </section>
    ))}
  </div>
);

const PreviewLockOverlay: React.FC<{
  userStatus: DashboardUserStatus;
  sampleDate: string | null;
  onUseSampleDate: () => void;
}> = ({ userStatus, sampleDate, onUseSampleDate }) => {
  if (userStatus === 'anonymous') {
    return (
      <div className="rounded-[10px] border border-[var(--qcl-border)] bg-white/95 px-3 py-2 text-center shadow-[var(--qcl-shadow-sm)]">
        <p className="text-[11px] font-semibold text-[var(--qcl-text)]">Sign in to view complete rankings</p>
        <Link
          to="/login"
          className="mt-2 inline-flex min-h-[32px] items-center rounded-[8px] border border-[var(--qcl-border)] px-3 py-1 text-[11px] font-semibold text-[var(--qcl-text)] transition hover:bg-[var(--qcl-bg)]"
        >
          Sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="rounded-[10px] border border-[var(--qcl-border)] bg-white/95 px-3 py-2 text-center shadow-[var(--qcl-shadow-sm)]">
      <p className="text-[11px] font-semibold text-[var(--qcl-text)]">Upgrade to view the latest full rankings</p>
      <a
        href="https://www.quantchecklabs.com/subscribe"
        className="mt-2 inline-flex min-h-[32px] items-center justify-center rounded-[8px] bg-[var(--qcl-accent)] px-3 py-1 text-[11px] font-semibold text-white transition hover:bg-[var(--qcl-accent-hover)]"
      >
        Subscribe to See
      </a>
      <button
        type="button"
        onClick={onUseSampleDate}
        disabled={!sampleDate}
        className={`mt-1.5 inline-flex min-h-[32px] items-center justify-center rounded-[8px] border px-3 py-1 text-[11px] font-semibold transition ${
          sampleDate
            ? 'border-[var(--qcl-border)] text-[var(--qcl-text)] hover:bg-[var(--qcl-bg)]'
            : 'cursor-not-allowed border-[var(--qcl-border-light)] text-[var(--qcl-text-muted)]'
        }`}
      >
        Show 30-Day Delayed Sample
      </button>
    </div>
  );
};

const CrossAssetDashboard: React.FC = () => {
  const { userEmail, isPremium, authReady } = useAuth();
  const authenticatedFetch = useAuthenticatedFetch();
  const authenticatedFetchRef = useRef(authenticatedFetch);
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<CrossAssetDashboardResponse | null>(null);
  const [dataSource, setDataSource] = useState<'snapshot' | 'legacy'>('legacy');
  const [streakMetricsByKey, setStreakMetricsByKey] = useState<Record<string, StreakResponseItem>>({});
  const [streakMetricsLoading, setStreakMetricsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reloadCounter, setReloadCounter] = useState(0);
  const [error, setError] = useState<{ message: string; unauthorized: boolean } | null>(null);
  const dataRef = useRef<CrossAssetDashboardResponse | null>(null);
  const dataDateRef = useRef<string | null>(null);
  const dataSourceRef = useRef<'snapshot' | 'legacy'>('legacy');
  const currentSnapshotPathRef = useRef<string | null>(null);
  const skipNextFetchRef = useRef<{
    date: string;
    userEmail: string | null;
    reloadCounter: number;
  } | null>(null);

  const requestedDate = searchParams.get('date');
  const selectedDate = isIsoDate(requestedDate) ? requestedDate : null;
  const historicalDateRefreshToken = useHistoricalDateRefreshToken(selectedDate, () => {
    setLoading(true);
    setError(null);
  });
  const pickerDateValue = selectedDate || data?.date || '';
  const pickerDateLabel = pickerDateValue ? formatSearchDate(pickerDateValue) : 'Most recent';

  const retryFetch = () => {
    setReloadCounter((current) => current + 1);
  };

  const handleDashboardDateChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const rawDate = event.target.value;
      const nextDate = rawDate && rawDate < MIN_DASHBOARD_DATE ? MIN_DASHBOARD_DATE : rawDate;
      setSearchParams((previousParams) => {
        const next = new URLSearchParams(previousParams);
        if (nextDate) {
          next.set('date', nextDate);
        } else {
          next.delete('date');
        }
        return next;
      });
    },
    [setSearchParams]
  );

  const handleUseSampleDate = useCallback(
    (sampleDate: string | null) => {
      if (!sampleDate) return;
      setSearchParams((previousParams) => {
        const next = new URLSearchParams(previousParams);
        next.set('date', sampleDate);
        return next;
      });
    },
    [setSearchParams]
  );

  useEffect(() => {
    authenticatedFetchRef.current = authenticatedFetch;
  }, [authenticatedFetch]);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    dataDateRef.current = data?.date || null;
  }, [data?.date]);

  useEffect(() => {
    dataSourceRef.current = dataSource;
  }, [dataSource]);

  useEffect(() => {
    let cancelled = false;

    if (!authReady) {
      return () => {
        cancelled = true;
      };
    }

    if (
      selectedDate &&
      dataDateRef.current === selectedDate &&
      skipNextFetchRef.current &&
      skipNextFetchRef.current.date === selectedDate &&
      skipNextFetchRef.current.userEmail === userEmail &&
      skipNextFetchRef.current.reloadCounter === reloadCounter
    ) {
      skipNextFetchRef.current = null;
      return;
    }

    const fetchDashboard = async () => {
      setLoading(true);
      setError(null);

      try {
        const fetchFromLegacyApi = async (): Promise<CrossAssetDashboardResponse> => {
          const endpointParams = new URLSearchParams();
          if (selectedDate) {
            endpointParams.set('date', selectedDate);
          }
          if (historicalDateRefreshToken) {
            endpointParams.set('_ts', historicalDateRefreshToken);
          }
          const endpoint = endpointParams.toString()
            ? `/api/dashboard/cross-asset?${endpointParams.toString()}`
            : '/api/dashboard/cross-asset';
          const response = await authenticatedFetchRef.current(endpoint);

          if (!response.ok) {
            const payload = await response.json().catch(() => null);
            const fallbackError =
              response.status === 401
                ? 'Please log in to access dashboard data.'
                : 'Unable to load dashboard data.';
            const message = payload?.message || payload?.error || fallbackError;
            const requestError = new Error(message);
            (requestError as Error & { status?: number }).status = response.status;
            throw requestError;
          }

          const payload = (await response.json()) as CrossAssetDashboardResponse;
          if (!payload || !payload.asset_classes) {
            const responseError = new Error('Unexpected dashboard response format.');
            (responseError as Error & { status?: number }).status = 500;
            throw responseError;
          }
          return payload;
        };

        const fetchFromSnapshots = async (): Promise<CrossAssetDashboardResponse | null> => {
          let runKind: SnapshotRunKind | null = null;
          let tier: 'preview' | 'delayed' | 'full' | null = null;
          let resolvedDate: string | null = null;
          let refPath: string | null = null;
          let snapshotPath: string | null = null;
          let failureStage: SnapshotFailureStage | null = 'meta';

          try {
            const metaResponse = await fetchSnapshotResponse(SNAPSHOT_META_PATH, {
              noStore: true,
              cacheBustToken: historicalDateRefreshToken,
            });
            if (!metaResponse.ok) {
              throw createSnapshotRequestFailure({
                stage: 'meta',
                message: `Snapshot meta request failed with status ${metaResponse.status}`,
                statusCode: metaResponse.status,
                path: SNAPSHOT_META_PATH,
              });
            }
            const meta = (await metaResponse.json()) as CrossAssetSnapshotMeta;
            const rawLatestDate = meta?.latest_date || null;
            const latestDate = isIsoDate(rawLatestDate) ? rawLatestDate : null;
            if (!latestDate) {
              throw createSnapshotRequestFailure({
                stage: 'meta',
                message: 'Snapshot meta is missing latest_date',
                path: SNAPSHOT_META_PATH,
              });
            }

            resolvedDate = selectedDate || latestDate;
            if (!isIsoDate(resolvedDate) || resolvedDate < MIN_DASHBOARD_DATE) {
              return null;
            }

            const rawCutoffDate = meta.cutoff_date_utc || null;
            const cutoffDate = isIsoDate(rawCutoffDate)
              ? rawCutoffDate
              : getUtcCutoffDate();
            runKind =
              resolvedDate === latestDate && isSnapshotRunKind(meta.latest_run_kind)
                ? meta.latest_run_kind
                : 'final';
            tier = !userEmail
              ? 'preview'
              : isPremium
              ? 'full'
              : resolvedDate <= cutoffDate
              ? 'delayed'
              : 'preview';

            const mintSnapshotEntitlements = async (): Promise<boolean> => {
              try {
                const entitlementResponse = await authenticatedFetchRef.current('/api/auth/snapshots-cookie', {
                  method: 'POST',
                  includeAccessTokenInBody: false,
                });
                if (!entitlementResponse.ok) {
                  console.warn(
                    `Snapshot entitlement mint failed (${entitlementResponse.status}); falling back to legacy API.`
                  );
                  return false;
                }
                return true;
              } catch (entitlementError) {
                console.warn(
                  'Failed to mint snapshot entitlement cookie after 403; falling back to legacy API.',
                  entitlementError
                );
                return false;
              }
            };

            failureStage = 'ref';
            refPath = resolveSnapshotTemplatePath(
                tier === 'preview'
                  ? meta?.cross_asset?.preview_ref_template || DEFAULT_CROSS_ASSET_REF_TEMPLATE
                  : tier === 'delayed'
                  ? meta?.cross_asset?.delayed_ref_template || DEFAULT_CROSS_ASSET_REF_TEMPLATE
                  : meta?.cross_asset?.full_ref_template || DEFAULT_CROSS_ASSET_REF_TEMPLATE,
                {
                  tier,
                  run_kind: runKind,
                  date: resolvedDate,
                }
              );
            snapshotPath = await resolveSnapshotPayloadPath({
              refPath,
              retryOnForbidden: tier !== 'preview' && !!userEmail,
              mintEntitlements: tier !== 'preview' && !!userEmail ? mintSnapshotEntitlements : undefined,
              cacheBustToken: historicalDateRefreshToken,
              authenticatedFetch: authenticatedFetchRef.current,
            });

            if (
              snapshotPath === currentSnapshotPathRef.current &&
              dataSourceRef.current === 'snapshot' &&
              dataRef.current
            ) {
              return dataRef.current;
            }

            failureStage = 'payload';
            const snapshotResponse = await fetchSnapshotResponse(snapshotPath, {
              retryOnForbidden: tier !== 'preview' && !!userEmail,
              mintEntitlements: tier !== 'preview' && !!userEmail ? mintSnapshotEntitlements : undefined,
              cacheBustToken: historicalDateRefreshToken,
              authenticatedFetch: authenticatedFetchRef.current,
            });
            if (!snapshotResponse.ok) {
              throw createSnapshotRequestFailure({
                stage: 'payload',
                message: `Snapshot payload request failed with status ${snapshotResponse.status}`,
                statusCode: snapshotResponse.status,
                path: snapshotPath,
              });
            }

            const payload = (await snapshotResponse.json()) as CrossAssetDashboardResponse;
            if (!payload || !payload.asset_classes) {
              throw createSnapshotRequestFailure({
                stage: 'payload',
                message: 'Snapshot payload shape was invalid',
                path: snapshotPath,
              });
            }

            const resolvedUserStatus: DashboardUserStatus = !userEmail
              ? 'anonymous'
              : isPremium
              ? 'premium'
              : 'free';
            const resolvedAccessLevel: DashboardAccessLevel = tier === 'preview' ? 'PREVIEW' : 'FULL';

            payload.access = {
              level: resolvedAccessLevel,
              user_status: resolvedUserStatus,
              cutoff_date: cutoffDate,
              sample_date: cutoffDate,
            };
            payload.date = payload.date || resolvedDate;
            currentSnapshotPathRef.current = snapshotPath;

            return payload;
          } catch (snapshotError) {
            console.warn('Failed to fetch cross-asset snapshot; falling back to legacy API.', snapshotError);
            const snapshotFailure = readSnapshotRequestFailure(snapshotError);
            const stage = snapshotFailure.stage || failureStage;
            const statusCode = snapshotFailure.statusCode;
            reportCdnFallback({
              surface: 'cross-asset-home',
              apiPath: '/api/dashboard/cross-asset',
              manifestPath: SNAPSHOT_META_PATH,
              refPath,
              snapshotPath,
              userStatus: !userEmail ? 'anonymous' : isPremium ? 'premium' : 'free',
              isPremium,
              stage,
              statusCode,
              details: {
                selectedDate: selectedDate || null,
                resolvedDate,
                runKind,
                tier,
              },
              reason: snapshotError instanceof Error ? snapshotError.message : 'Unknown snapshot failure',
            });
            return null;
          }
        };

        let payload = await fetchFromSnapshots();
        if (payload) {
          setDataSource('snapshot');
        } else {
          payload = await fetchFromLegacyApi();
          setDataSource('legacy');
          currentSnapshotPathRef.current = null;
        }

        if (cancelled) return;
        setData(payload);
        initAnalytics();

        if (payload.date && payload.date !== selectedDate) {
          skipNextFetchRef.current = {
            date: payload.date,
            userEmail,
            reloadCounter,
          };
          setSearchParams(
            (previousParams) => {
              const next = new URLSearchParams(previousParams);
              next.set('date', payload.date);
              return next;
            },
            { replace: true }
          );
        }
      } catch (fetchError: any) {
        if (cancelled) return;
        const message = fetchError?.message || 'Unable to load dashboard data.';
        const unauthorized = fetchError?.status === 401;
        setError({ message, unauthorized });
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchDashboard();

    return () => {
      cancelled = true;
    };
  }, [authReady, historicalDateRefreshToken, isPremium, reloadCounter, selectedDate, setSearchParams, userEmail]);

  useEffect(() => {
    const accessLevel = data?.access?.level || 'FULL';
    if (!data || accessLevel !== 'FULL') {
      setStreakMetricsByKey({});
      setStreakMetricsLoading(false);
      return;
    }
    if (dataSource === 'snapshot') {
      setStreakMetricsByKey({});
      setStreakMetricsLoading(false);
      return;
    }

    let cancelled = false;
    const dashboardDate = data.date;
    const macroAsOfDate = data.asset_classes.macro.as_of_date || dashboardDate;
    const bondsAsOfDate = data.asset_classes.bonds.as_of_date || dashboardDate;
    const commoditiesAsOfDate = data.asset_classes.commodities.as_of_date || dashboardDate;
    const currenciesAsOfDate = data.asset_classes.currencies.as_of_date || dashboardDate;
    const cryptoAsOfDate = data.asset_classes.crypto.as_of_date || dashboardDate;
    const countriesAsOfDate = data.asset_classes.equities.countries.as_of_date || dashboardDate;
    const spSectorsAsOfDate = data.asset_classes.equities.sp_sectors.as_of_date || dashboardDate;
    const etfsAsOfDate = data.asset_classes.equities.etfs.as_of_date || dashboardDate;

    const items: StreakRequestItem[] = [];
    const appendItems = (
      assets: DashboardAsset[],
      matrixgroup: string,
      asOfDate: string,
      topLimit: number,
      rankBasis: StreakRankBasis
    ) => {
      for (const asset of assets) {
        if (!asset?.ticker) continue;
        items.push({
          key: buildStreakKey(matrixgroup, asOfDate, rankBasis, topLimit, asset.ticker),
          ticker: asset.ticker,
          matrixgroup,
          as_of_date: asOfDate,
          top_limit: topLimit,
          rank_basis: rankBasis,
        });
      }
    };

    appendItems(toSafeAssets(data.asset_classes.macro.top_assets), 'Macro', macroAsOfDate, 5, 'rank');
    appendItems(toSafeAssets(data.asset_classes.bonds.top_assets), 'Bonds', bondsAsOfDate, 3, 'rank');
    appendItems(
      toSafeAssets(data.asset_classes.commodities.top_assets),
      'Commodities',
      commoditiesAsOfDate,
      5,
      'rank'
    );
    appendItems(
      toSafeAssets(data.asset_classes.currencies.top_assets),
      'Currencies',
      currenciesAsOfDate,
      1,
      'rank'
    );
    appendItems(toSafeAssets(data.asset_classes.crypto.top_assets), 'All Crypto', cryptoAsOfDate, 5, 'rsi5_rank');
    appendItems(
      toSafeAssets(data.asset_classes.equities.countries.top_assets),
      'Countries',
      countriesAsOfDate,
      5,
      'rank'
    );
    appendItems(
      toSafeAssets(data.asset_classes.equities.sp_sectors.top_assets),
      'S&P 500 Sectors',
      spSectorsAsOfDate,
      3,
      'rank'
    );
    appendItems(toSafeAssets(data.asset_classes.equities.etfs.top_assets), 'Equity ETFs', etfsAsOfDate, 5, 'rank');

    const uniqueItems = Array.from(
      items.reduce((accumulator, item) => {
        accumulator.set(item.key, item);
        return accumulator;
      }, new Map<string, StreakRequestItem>()).values()
    );

    if (uniqueItems.length === 0) {
      setStreakMetricsByKey({});
      setStreakMetricsLoading(false);
      return;
    }

    setStreakMetricsLoading(true);
    setStreakMetricsByKey({});

    const fetchStreakMetrics = async () => {
      try {
        const streakRequestItems = uniqueItems.map(
          ({ ticker, matrixgroup, as_of_date, top_limit, rank_basis }) => ({
            ticker,
            matrixgroup,
            as_of_date,
            top_limit,
            rank_basis,
          })
        );
        const endpoint = '/api/dashboard/cross-asset-streaks';
        const response = await authenticatedFetchRef.current(endpoint, {
          method: 'POST',
          body: JSON.stringify({ items: streakRequestItems }),
          includeAccessTokenInBody: false,
        });

        if (!response.ok) {
          throw new Error(`Failed to load streak metrics (${response.status})`);
        }

        const payload = (await response.json()) as { items?: StreakResponseItem[] };
        if (cancelled) return;

        const mapped = (payload.items || []).reduce((accumulator, item) => {
          if (item?.key) {
            accumulator[item.key] = item;
          }
          return accumulator;
        }, {} as Record<string, StreakResponseItem>);

        setStreakMetricsByKey(mapped);
      } catch (error) {
        if (cancelled) return;
        console.error('⚠️ Failed to load cross-asset streak metrics:', error);
        setStreakMetricsByKey({});
      } finally {
        if (!cancelled) {
          setStreakMetricsLoading(false);
        }
      }
    };

    void fetchStreakMetrics();

    return () => {
      cancelled = true;
    };
  }, [data, dataSource, userEmail]);

  const content = useMemo(() => {
    if (!data) return null;

    const access = data.access || {
      level: 'FULL' as DashboardAccessLevel,
      user_status: userEmail ? ('free' as DashboardUserStatus) : ('anonymous' as DashboardUserStatus),
      cutoff_date: MIN_DASHBOARD_DATE,
      sample_date: MIN_DASHBOARD_DATE,
    };
    const previewMode = access.level === 'PREVIEW';
    const sampleDate = access.sample_date || access.cutoff_date || null;
    const canOpenDetailRoutes = access.level === 'FULL';

    const macro = data.asset_classes.macro;
    const bonds = data.asset_classes.bonds;
    const commodities = data.asset_classes.commodities;
    const currencies = data.asset_classes.currencies;
    const crypto = data.asset_classes.crypto;
    const equities = data.asset_classes.equities;
    const dashboardDate = data.date;
    const macroAsOfDate = macro.as_of_date || dashboardDate;
    const bondsAsOfDate = bonds.as_of_date || dashboardDate;
    const commoditiesAsOfDate = commodities.as_of_date || dashboardDate;
    const currenciesAsOfDate = currencies.as_of_date || dashboardDate;
    const cryptoAsOfDate = crypto.as_of_date || dashboardDate;
    const countriesAsOfDate = equities.countries.as_of_date || dashboardDate;
    const spSectorsAsOfDate = equities.sp_sectors.as_of_date || dashboardDate;
    const etfsAsOfDate = equities.etfs.as_of_date || dashboardDate;
    const lockOverlay = previewMode ? (
      <PreviewLockOverlay
        userStatus={access.user_status}
        sampleDate={sampleDate}
        onUseSampleDate={() => handleUseSampleDate(sampleDate)}
      />
    ) : null;

    const applyStreakMetrics = (
      assets: DashboardAsset[],
      matrixgroup: string,
      asOfDate: string,
      topLimit: number,
      rankBasis: StreakRankBasis
    ): DashboardAsset[] =>
      assets.map((asset) => {
        const key = buildStreakKey(matrixgroup, asOfDate, rankBasis, topLimit, asset.ticker);
        const metric = streakMetricsByKey[key];
        return {
          ...asset,
          streak_days: metric ? metric.streak_days : asset.streak_days ?? null,
          streak_start_date: metric ? metric.streak_start_date : asset.streak_start_date ?? null,
          streak_price_change_abs: metric
            ? metric.price_change_abs
            : asset.streak_price_change_abs ?? null,
          streak_price_change_pct: metric
            ? metric.price_change_pct
            : asset.streak_price_change_pct ?? null,
          streak_loading:
            streakMetricsLoading &&
            !metric &&
            asset.streak_days === undefined &&
            asset.streak_price_change_pct === undefined,
        };
      });

    const limitPreviewAssets = (assets: DashboardAsset[]): DashboardAsset[] =>
      previewMode ? assets.slice(0, 1) : assets;

    const macroAssets = limitPreviewAssets(
      applyStreakMetrics(toSafeAssets(macro.top_assets), 'Macro', macroAsOfDate, 5, 'rank')
    );
    const macroRepresentation = buildMacroRepresentationSummary(macroAssets);
    const bondsAssets = limitPreviewAssets(
      applyStreakMetrics(toSafeAssets(bonds.top_assets), 'Bonds', bondsAsOfDate, 3, 'rank')
    );
    const commoditiesAssets = limitPreviewAssets(
      applyStreakMetrics(toSafeAssets(commodities.top_assets), 'Commodities', commoditiesAsOfDate, 5, 'rank')
    );
    const currenciesAssets = limitPreviewAssets(
      applyStreakMetrics(toSafeAssets(currencies.top_assets), 'Currencies', currenciesAsOfDate, 1, 'rank')
    );
    const cryptoAssets = limitPreviewAssets(
      applyStreakMetrics(toSafeAssets(crypto.top_assets), 'All Crypto', cryptoAsOfDate, 5, 'rsi5_rank')
    );
    const countriesAssets = limitPreviewAssets(
      applyStreakMetrics(toSafeAssets(equities.countries.top_assets), 'Countries', countriesAsOfDate, 5, 'rank')
    );
    const etfAssets = limitPreviewAssets(
      applyStreakMetrics(toSafeAssets(equities.etfs.top_assets), 'Equity ETFs', etfsAsOfDate, 5, 'rank')
    );
    const sectorAssets = limitPreviewAssets(
      applyStreakMetrics(toSafeAssets(equities.sp_sectors.top_assets), 'S&P 500 Sectors', spSectorsAsOfDate, 3, 'rank')
    );

    const equitiesSegments: EquitiesSegment[] = [
      {
        key: 'global',
        label: '🌍 Global (Countries MSCI ETFs)',
        assets: countriesAssets,
        asOfDate: countriesAsOfDate,
      },
      {
        key: 'thematic',
        label: '🧩 Thematic ETFs',
        assets: etfAssets,
        asOfDate: etfsAsOfDate,
      },
      {
        key: 'sp500',
        label: '🇺🇸 S&P 500 Sectors',
        assets: sectorAssets,
        asOfDate: spSectorsAsOfDate,
      },
    ];

    const cardTiePriority: Record<'equities' | 'commodities' | 'crypto' | 'bonds' | 'currencies', number> = {
      equities: 0,
      commodities: 1,
      crypto: 2,
      bonds: 3,
      currencies: 4,
    };

    const macroCard = (
      <AssetClassTower
        key="macro-card"
        name="Macro"
        color="var(--qcl-macro)"
        selectedDate={dashboardDate}
        asOfDate={macroAsOfDate}
        cardPath={canOpenDetailRoutes ? '/dashboards/macro' : undefined}
        assetClickable={canOpenDetailRoutes}
        assets={macroAssets}
        previewLocked={previewMode}
        lockOverlay={lockOverlay}
        macroRepresentation={macroRepresentation}
        showMacroAssetClassPills
      />
    );

    const orderedCards = [
      macroCard,
      ...[
        {
          key: 'bonds' as const,
          allocation: Number(bonds.allocation || 0),
          element: (
            <AssetClassTower
              key="bonds-card"
              name="Bonds"
              color="var(--qcl-bonds)"
              selectedDate={dashboardDate}
              asOfDate={bondsAsOfDate}
              cardPath={canOpenDetailRoutes ? '/dashboards/bonds' : undefined}
              assetClickable={canOpenDetailRoutes}
              assets={bondsAssets}
              previewLocked={previewMode}
              lockOverlay={lockOverlay}
            />
          ),
        },
        {
          key: 'commodities' as const,
          allocation: Number(commodities.allocation || 0),
          element: (
            <AssetClassTower
              key="commodities-card"
              name="Commodities"
              color="var(--qcl-cmdty)"
              selectedDate={dashboardDate}
              asOfDate={commoditiesAsOfDate}
              cardPath={canOpenDetailRoutes ? '/dashboards/commodities' : undefined}
              assetClickable={canOpenDetailRoutes}
              assets={commoditiesAssets}
              previewLocked={previewMode}
              lockOverlay={lockOverlay}
            />
          ),
        },
        {
          key: 'currencies' as const,
          allocation: Number(currencies.allocation || 0),
          element: (
            <AssetClassTower
              key="currencies-card"
              name="Currencies"
              color="var(--qcl-fx)"
              selectedDate={dashboardDate}
              asOfDate={currenciesAsOfDate}
              cardPath={canOpenDetailRoutes ? '/dashboards/currencies' : undefined}
              assetClickable={false}
              assets={currenciesAssets}
              previewLocked={previewMode}
              lockOverlay={lockOverlay}
            />
          ),
        },
        {
          key: 'crypto' as const,
          allocation: Number(crypto.allocation || 0),
          element: (
            <AssetClassTower
              key="crypto-card"
              name="Cryptocurrency"
              color="var(--qcl-crypto)"
              selectedDate={dashboardDate}
              asOfDate={cryptoAsOfDate}
              cardPath={canOpenDetailRoutes ? '/dashboards/crypto' : undefined}
              assetClickable={false}
              rankChangeBasis="RSI5 rank"
              assets={cryptoAssets}
              previewLocked={previewMode}
              lockOverlay={lockOverlay}
            />
          ),
        },
        {
          key: 'equities' as const,
          allocation: Number(equities.allocation || 0),
          element: (
            <EquitiesTower
              key="equities-card"
              selectedDate={dashboardDate}
              segments={equitiesSegments}
              previewLocked={previewMode}
              lockOverlay={lockOverlay}
            />
          ),
        },
      ]
        .sort((left, right) => {
          if (right.allocation !== left.allocation) {
            return right.allocation - left.allocation;
          }
          return cardTiePriority[left.key] - cardTiePriority[right.key];
        })
        .map((card) => card.element),
    ];

    return (
      <>
        {previewMode ? (
          <section className="rounded-[14px] border border-[rgba(232,147,12,0.25)] bg-[rgba(232,147,12,0.08)] px-4 py-3">
            <p className="text-[12px] text-[#8e5a00]">
              {access.user_status === 'anonymous'
                ? 'Preview mode: top-ranked assets are visible. Sign in to unlock full rankings.'
                : 'Preview mode: your account can view delayed data. Upgrade to access the latest full rankings.'}
            </p>
            {access.user_status === 'anonymous' ? (
              <div className="mt-2">
                <Link
                  to="/login"
                  className="inline-flex min-h-[40px] items-center rounded-[9px] border border-[rgba(142,90,0,0.32)] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#7d4f00] transition hover:bg-[rgba(255,255,255,0.88)]"
                >
                  Sign in to continue
                </Link>
              </div>
            ) : (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <a
                  href="https://www.quantchecklabs.com/subscribe"
                  className="inline-flex min-h-[40px] items-center rounded-[9px] bg-[#8e5a00] px-3 py-1.5 text-[12px] font-semibold text-white transition hover:opacity-90"
                >
                  Subscribe to See
                </a>
                <button
                  type="button"
                  onClick={() => handleUseSampleDate(sampleDate)}
                  disabled={!sampleDate}
                  className={`inline-flex min-h-[40px] items-center rounded-[9px] border px-3 py-1.5 text-[12px] font-semibold transition ${
                    sampleDate
                      ? 'border-[rgba(142,90,0,0.32)] bg-white text-[#7d4f00] hover:bg-[rgba(255,255,255,0.88)]'
                      : 'cursor-not-allowed border-[rgba(142,90,0,0.16)] bg-[rgba(255,255,255,0.62)] text-[#b07f38]'
                  }`}
                >
                  Show 30-Day Delayed Sample
                </button>
              </div>
            )}
          </section>
        ) : null}
        <DashboardDatePicker
          dateValue={pickerDateValue}
          dateLabel={pickerDateLabel}
          onDateChange={handleDashboardDateChange}
        />
        {orderedCards}
      </>
    );
  }, [
    data,
    handleDashboardDateChange,
    handleUseSampleDate,
    pickerDateLabel,
    pickerDateValue,
    streakMetricsByKey,
    streakMetricsLoading,
    userEmail,
  ]);

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (error) {
    return (
      <div className="mx-auto max-w-[920px] px-4 py-[14px] md:px-7 md:py-5">
        <section className="rounded-[14px] border border-[var(--qcl-border)] bg-white p-6 shadow-[var(--qcl-shadow)]">
          <h2 className="text-[20px] font-bold tracking-[-0.02em] text-[var(--qcl-text)]">Unable to load dashboard</h2>
          <p className="mt-2 text-[14px] text-[var(--qcl-text-dim)]">{error.message}</p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={retryFetch}
              className="min-h-[44px] rounded-[10px] bg-[var(--qcl-accent)] px-4 py-2 text-[13px] font-semibold text-white transition hover:bg-[var(--qcl-accent-hover)]"
            >
              Retry
            </button>
            {error.unauthorized && !userEmail && (
              <Link
                to="/login"
                className="min-h-[44px] rounded-[10px] border border-[var(--qcl-border)] px-4 py-2 text-[13px] font-semibold text-[var(--qcl-text)] transition hover:bg-[var(--qcl-bg)]"
              >
                Go to login
              </Link>
            )}
          </div>
        </section>
      </div>
    );
  }

  return <div className="mx-auto max-w-[920px] space-y-3 px-4 py-[14px] md:space-y-4 md:px-7 md:py-5">{content}</div>;
};

export default CrossAssetDashboard;
